/**
 * 자동결제 안내 — 카드를 받기 전에 금액을 말한다
 * ---------------------------------------------------------------------------
 * ## 왜 카드보다 먼저인가
 * 카드 정보를 다 넣게 한 뒤에 "사실 매달 99,000원이 나갑니다" 를 보여 주면,
 * 그만두고 싶어도 이미 PG 화면을 지나온 뒤다. 금액을 먼저 보여 주고 동의를
 * 받은 다음에 카드 창을 연다.
 *
 * ## 금액은 서버가 계산한다
 * 화면에 요금을 적어 두면 요금제가 바뀐 날 틀린 금액에 동의를 받게 된다.
 * 서버가 지금 구독 그대로 계산해 준 값을 그대로 보여 준다 — 초과 사용분까지
 * 포함한 실제 청구 기준이다.
 *
 * ## 동의하지 않으면 등록되지 않는다
 * 서버도 같은 것을 다시 본다. 이 창을 건너뛰고 API 를 직접 불러도
 * `AUTOPAY_CONSENT_REQUIRED` 로 거절되며, 거절 응답에 금액이 함께 온다.
 */

import { Modal, Typography, Table } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';

const { Text, Paragraph } = Typography;

export interface AutopayQuoteLine {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface AutopayQuote {
  companyName: string;
  lines: AutopayQuoteLine[];
  monthlyAmount: number;
  vatAmount: number;
  totalAmount: number;
  firstBillingAt: string | null;
  billingDay: number;
  trialEndsAt: string | null;
}

const won = (n: number) => Math.round(n).toLocaleString('ko-KR');
const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('ko-KR', { dateStyle: 'long' }) : '-';

export async function fetchAutopayQuote(): Promise<AutopayQuote> {
  return (await apiClient.get<AutopayQuote>('/billing/autopay/quote')).data;
}

/** 거절 응답에 실려 온 금액. 화면이 먼저 물어보지 못했을 때 쓴다. */
export function quoteFromError(err: NormalizedError): AutopayQuote | null {
  const q = err.payload?.quote;
  return q && typeof q === 'object' ? (q as AutopayQuote) : null;
}

/**
 * 「이 내용에 동의합니다」를 받는다.
 *
 * 체크하지 않으면 「동의하고 카드 등록」이 눌리지 않는다. 기본값을 체크됨으로
 * 두지 않는 이유 — 눌러서 넘어간 것과 읽고 넘어간 것은 다르고, 분쟁에서
 * 그 차이가 전부다.
 */
export function askAutopayConsent(quote: AutopayQuote): Promise<boolean> {
  return new Promise((resolve) => {
    let agreed = false;

    const modal = Modal.confirm({
      title: '자동결제 안내',
      icon: <CreditCardOutlined style={{ color: '#1677ff' }} />,
      width: 680,
      okText: '동의하고 카드 등록',
      cancelText: '취소',
      okButtonProps: { disabled: true },
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          <Paragraph style={{ marginBottom: 10 }}>
            결제수단을 등록하시면 아래 내용으로 <b>매월 자동 결제</b>됩니다.
          </Paragraph>

          <Table<AutopayQuoteLine>
            rowKey={(r) => r.description}
            size="small"
            pagination={false}
            dataSource={quote.lines}
            style={{ marginBottom: 12 }}
            locale={{ emptyText: '청구 항목이 없습니다.' }}
            columns={[
              { title: '항목', dataIndex: 'description' },
              {
                title: '금액',
                dataIndex: 'amount',
                width: 120,
                align: 'right',
                render: (v: number) => `${won(v)}원`,
              },
            ]}
            summary={() => (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>공급가액</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    {won(quote.monthlyAmount)}원
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>부가세</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    {won(quote.vatAmount)}원
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <b>월 청구액</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <b style={{ fontSize: 15 }}>{won(quote.totalAmount)}원</b>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />

          <div
            style={{
              border: '1px solid #d6e4ff',
              background: '#f0f5ff',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 12,
            }}
          >
            <div>
              · 첫 청구 예정일 : <b>{day(quote.firstBillingAt)}</b>
              {quote.trialEndsAt && (
                <Text type="secondary">
                  {' '}
                  (무료 체험 종료 {day(quote.trialEndsAt)} 다음 날)
                </Text>
              )}
            </div>
            <div>
              · 이후 매월 <b>{quote.billingDay}일</b>에 등록하신 카드로 자동 결제됩니다
            </div>
            <div>· 결제 결과는 메일로 알려 드리며, 청구서는 「과금」 화면에서 보실 수 있습니다</div>
          </div>

          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
            <b>금액이 달라질 수 있습니다.</b> 구성원을 계약 인원보다 늘리거나 저장공간·트래픽
            한도를 넘겨 쓰면 그만큼 더해져 청구됩니다. 늘어나는 금액은 늘리는 시점에 화면에서
            확인하실 수 있습니다. 결제수단은 언제든 이 화면에서 바꾸거나 삭제하실 수 있고,
            삭제하면 다음 달부터 자동 결제가 멈춥니다.
          </Paragraph>

          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              padding: '10px 12px',
              border: '1px solid #ffe58f',
              background: '#fffbe6',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              style={{ marginTop: 3 }}
              onChange={(e) => {
                agreed = e.target.checked;
                modal.update({ okButtonProps: { disabled: !agreed } });
              }}
            />
            <span>
              위 내용을 모두 확인했으며, <b>매월 자동 결제되는 것에 동의합니다.</b>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                동의하신 시각·금액·접속 IP 가 기록됩니다.
              </Text>
            </span>
          </label>
        </div>
      ),
      onOk: () => {
        if (!agreed) return Promise.reject(new Error('동의가 필요합니다'));
        resolve(true);
        return Promise.resolve();
      },
      onCancel: () => resolve(false),
    });
  });
}
